/**
 * AccessLabOrderModal.tsx
 *
 * Thin Access Labs adapter over the shared LabOrderModalCore, cloned from
 * VibrantLabOrderModal. The clinic pays Access, so there is no billing and no
 * diagnoses; the form keeps location, provider, patient demographics, tests,
 * presets (in the Location bar, as on Vibrant), the collection date and time,
 * and notes, which Vibrant leaves off.
 *
 * Differences from Vibrant:
 *  - Access's compendium is ~16,000 codes, so the test search waits for typing
 *    and says when nothing matched or the list was cut short, in the Admin
 *    page's words. A code Access sent with no name is listed under its code.
 *  - With no Access location, or no provider at the chosen one, the form says
 *    who can set it up and Send Order is disabled.
 *  - A refusal or an order that was saved but not sent is shown in plain words
 *    and the form stays open. After any failure other than a coded 422
 *    refusal (not sent, a server failure, a dropped connection, a timeout)
 *    Send Order is disabled until the form is closed, because the order may
 *    already be saved; it can be resent from the Labs tab's Access list
 *    (AccessSubTab).
 */
import { useMemo } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import {
  getAccessProviders,
  getAccessLocations,
  searchAccessTests,
  submitAccessOrder,
  getAccessPresets,
  getAccessPresetContent,
  saveAccessPreset,
  type AccessOrderPayload,
  type QuestOrderPayload,
} from '@/services/labs.service';
import { getErrorCode } from '@/services/apiClient';
import { ACCESS_TEST_SEARCH_MESSAGES, accessOrdersQueryKey, accessTestName } from '@/lib/accessLabs';
import { accessOrderErrorMessage } from '@/lib/accessLabsErrors';
import { LabOrderModalCore, type LabOrderConfig, type LabOrderSubmitResult, type ParsedLabPreset } from './LabOrderModalCore';
// Access presets and prior orders are stored in Vibrant's JSON shape.
import { parseVibrantPreset } from './vibrantPreset';

interface AccessLabOrderModalProps {
  patientId: number;
  open: boolean;
  onClose: () => void;
  onOrdered: () => void;
}

const SUBMIT_FAILED =
  "Your Access Labs order couldn't be sent just now. Check the Access orders list on the patient's Labs tab to see whether it was saved or sent before trying again, and contact support if it keeps happening.";

const SETUP_MESSAGES = {
  noLocation:
    "Access Labs isn't set up for your practice yet, so orders can't be sent. Ask your practice administrator to set up Access Labs.",
  noProvider:
    "No ordering provider is set up for Access Labs at this location, so orders can't be sent from it. Ask your practice administrator to set one up on the Access Labs admin page, under Providers.",
};

function toAccessOrder(payload: QuestOrderPayload): AccessOrderPayload {
  return {
    patientId: payload.patientId,
    firstName: payload.firstName,
    middleName: payload.middleName,
    lastName: payload.lastName,
    birthMonth: payload.birthMonth,
    birthDay: payload.birthDay,
    birthYear: payload.birthYear,
    street: payload.street,
    city: payload.city,
    state: payload.state,
    zip: payload.zip,
    phone: payload.phone,
    gender: payload.gender,
    collectionDate: payload.tests[0]?.collectionDate ?? '',
    providerId: payload.provider,
    routingId: payload.routingId,
    tests: payload.tests.map((t) => ({
      testCode: t.testCode,
      description: t.description ?? '',
      collectionDate: t.collectionDate,
      obrNotes: t.obrNotes,
    })),
    pidNotes: payload.pidNotes,
    internalNotes: payload.internalNotes,
    externalNotes: payload.externalNotes,
  };
}

function accessConfig(onNotSent: () => void): LabOrderConfig {
  return {
    vendor: 'access',
    title: 'Access Labs Order',
    // Presets carry a location, so they sit next to the Location picker.
    presetsInLocationBar: true,
    testsBeforeDetails: true,
    // The server sends the chart's name, birth date and gender.
    lockFilledIdentity: true,
    features: {
      presets: true,
      facilityProvider: false,
      providerPicker: true,
      locationPicker: true,
      // Too many codes to list: search only once something is typed.
      testSearchOnEmpty: false,
      billing: false,
      diagnoses: false,
      vitals: false,
      notes: true,
      collectionTime: true,
    },
    testSearchMessages: ACCESS_TEST_SEARCH_MESSAGES,
    describeTest: accessTestName,
    setupMessages: SETUP_MESSAGES,
    presetAdminPath: 'Admin > Integrations > Access Labs > Presets',
    // No location yet: only the codes orderable at every location.
    searchTests: async (query, routingId) => {
      const page = await searchAccessTests(query, routingId, { globalOnly: !routingId });
      return { tests: page.tests, capped: page.capped };
    },
    // Wrapped, not passed, so nothing is read from the service module until
    // the form calls it: the Labs tab mounts this closed, and its tests mock
    // the service with only what they use.
    getProviders: (routingId) => getAccessProviders(routingId),
    getLocations: () => getAccessLocations(),
    getPresets: (patientId, routingId) => getAccessPresets(patientId, routingId),
    getPresetContent: (type, id) => getAccessPresetContent(type, id),
    parsePreset: (content, kind, defaultDate): ParsedLabPreset => parseVibrantPreset(content, kind, defaultDate),
    savePreset: (payload: QuestOrderPayload & { presetTitle: string; presetShared: string }) =>
      saveAccessPreset({
        presetTitle: payload.presetTitle,
        presetShared: payload.presetShared,
        routingId: payload.routingId ?? '',
        tests: payload.tests.map((t) => ({
          testCode: t.testCode,
          description: t.description ?? '',
          obrNotes: t.obrNotes,
        })),
      }),
    submit: async (payload): Promise<LabOrderSubmitResult> => {
      try {
        const result = await submitAccessOrder(toAccessOrder(payload));
        return { ok: true, lines: [`Order #${result.orderId} sent to Access Labs.`] };
      } catch (err) {
        // Only a coded 422 refusal is known to have saved nothing. Anything
        // else (saved but not sent, a server failure, a dropped connection or
        // a timeout) may have saved the order or even sent it, so it is on the
        // patient's Access list and this form must not send it again (that
        // would be a second order).
        const refused = axios.isAxiosError(err) && err.response?.status === 422 && getErrorCode(err) !== null;
        if (!refused) onNotSent();
        return { ok: false, lines: [], errorMessage: accessOrderErrorMessage(err, SUBMIT_FAILED), lockSend: !refused };
      }
    },
  };
}

export function AccessLabOrderModal({ patientId, open, onClose, onOrdered }: AccessLabOrderModalProps) {
  const queryClient = useQueryClient();
  const config = useMemo(
    () => accessConfig(() => { void queryClient.invalidateQueries({ queryKey: accessOrdersQueryKey(patientId) }); }),
    [queryClient, patientId],
  );

  return (
    <LabOrderModalCore
      config={config}
      patientId={patientId}
      open={open}
      onClose={onClose}
      onOrdered={onOrdered}
    />
  );
}
